import { Box, Text } from "grommet";
import SvgBorder from "./SvgBorder";
import { memo, useMemo } from "react";
import { mergeProps } from "../../../utils/mergeUtils";

const defaultCardStyle = {
    size: 160,
    radius: 16,
    color: {
        color1: 'rgb(100, 100, 100)',
        color2: 'rgb(100, 100, 100)',
        angle: 180
    },
    shadow: {
        color: 'rgb(0, 0, 0)',
        horizontalOffset: 4,
        verticalOffset: 4,
        blur: 4
    },
    // image: {
    //     url: `${process.env.PUBLIC_URL}/images/bg_cs2_1.webp`,
    //     hueRotate: 0,
    //     saturate: 100
    // }
}

const defaultBorderStyle = {
    size: 170,
    radius: 16,
    width: 6,
    outline: {
        color1: "rgb(165, 0, 92)",
        color2: "rgb(165, 0, 92)",
        gradientAngle: 180
    },
    // image: {
    //     url: '',
    //     hueRotate: 0,
    //     saturate: 100
    // },
    shadow: {
        color: 'rgb(0, 0, 0)',
        horizontalOffset: 0,
        verticalOffset: 0,
        blur: 0
    }
}

const defaultTextStyle = {
    font: 'Roboto Mono',
    color: 'rgb(255, 255, 255)',
    fontWeight: 'bold',
    fontStyle: 'none',
    textDecoration: 'unset',
    textAlign: 'center',
    textTransform: 'uppercase',
    fontSize: 19,
    stroke: {
        width: 1,
        color: 'rgb(0, 0, 0)'
    },
    shadow: {
        color: 'rgb(0, 0, 0)',
        horizontalOffset: 1,
        verticalOffset: 1,
        blur: 2
    },
    lineHeight: 1.66,
    letterSpacing: 0.55,
    wordSpacing: 0,
    transform: {
        x: 0,
        y: 0
    },
    fontBlockWidth: 160
}

/**
 * 
 * @param {number} cardStyle.size - card size in pixels 
 * @param {number} cardStyle.radius - card radius in pixels
 * 
 * @param {number} cardStyle.color.angle - color gradient angle in degrees
 * @param {string} cardStyle.color.color1 - first gradient color
 * @param {string} cardStyle.color.color2 - second gradient color
 * 
 * @param {string} cardStyle.shadow.color - shadow color
 * @param {number} cardStyle.shadow.horizontalOffset - shadow horizontal offset
 * @param {number} cardStyle.shadow.verticalOffset - shadow vertical offset
 * @param {number} cardStyle.shadow.blur - shadow blur
 * 
 * @param {string} cardStyle.image.url - image url
 * @param {number} cardStyle.image.hueRotate - image hue rotate in degrees
 * @param {number} cardStyle.image.saturate - image saturate in percents
 * @returns 
 */
const getCardStyle = (cardStyle) => {

    const style = mergeProps(defaultCardStyle, cardStyle, { color: 'image' });
    
    let values = {
        height: `${style.size}px`,
        width: `${style.size}px`,
        borderRadius: `${style.radius}px`
    }
    
    if (style.color) {
        const color = style.color;
        values = { ...values, ...{
            background: `linear-gradient(${color.angle}deg, ${color.color1} 0%, ${color.color2} 100%)`,
        }}
    }
    
    if (style.image) {
        const image = style.image;
        values = { ...values, ...{
            backgroundImage: `url(${image.url})`,
            backgroundSize: 'cover',
            filter: `hue-rotate(${image.hueRotate}deg) saturate(${image.saturate}%)`,
        }}
    }

    if (style.shadow) {
        const shadow = style.shadow;
        values = { ...values, ...{
            filter: `drop-shadow(${shadow.color} ${shadow.horizontalOffset}px ${shadow.verticalOffset}px ${shadow.blur}px)`,
        }}
    }

    if (style.shadow && style.image) {
        const shadow = style.shadow;
        const image = style.image;
        values = { ...values, ...{
            filter: `drop-shadow(${shadow.color} ${shadow.horizontalOffset}px ${shadow.verticalOffset}px ${shadow.blur}px) hue-rotate(${image.hueRotate}deg) saturate(${image.saturate}%)`,
        }}
    }

    return values;
}

/**
 * @param {number} borderStyle.size - border size in pixels 
 * @param {number} borderStyle.radius - border radius in pixels
 * @param {number} borderStyle.width - border width in pixels
 * 
 * @param {string} borderStyle.outline.color1 - outline first gradient color
 * @param {string} borderStyle.outline.color2 - outline second gradient color
 * @param {number} borderStyle.outline.gradientAngle - outline gradient angle in degrees
 * 
 * @param {string} borderStyle.image.url - image url
 * @param {number} borderStyle.image.hueRotate - image hue rotate in degrees
 * @param {number} borderStyle.image.saturate - image saturate in percents
 * 
 * @param {string} borderStyle.shadow.color - shadow color
 * @param {number} borderStyle.shadow.horizontalOffset - shadow horizontal offset
 * @param {number} borderStyle.shadow.verticalOffset - shadow vertical offset
 * @param {number} borderStyle.shadow.blur - shadow blur
 * 
 * @returns 
 */
const getBorderStyle = (borderStyle) => {

    const style = mergeProps(defaultBorderStyle, borderStyle, { outline: 'image' });

    let values = {};

    if (style.outline) {
        const outline = style.outline;
        values = { ...values, ...{
            width: style.size,
            height: style.size,
            radius: style.radius,
            borderWidth: style.width,
            color1: outline.color1,
            color2: outline.color2,
            gradientAngle: outline.gradientAngle,
        }}
    }

    if (style.image) {
        const image = style.image;
        values = { ...values, ...{
            width: `${style.size}px`,
            height: `${style.size}px`,
            radius: `${style.radius}px`,
            backgroundImage: `url(${image.url})`,
            backgroundSize: 'cover',
            filter: `hue-rotate(${image.hueRotate}deg) saturate(${image.saturate}%)`,
        }}
    }

    if (style.shadow) {
        const shadow = style.shadow;
        values = { ...values, ...{
            filter: `drop-shadow(${shadow.color} ${shadow.horizontalOffset}px ${shadow.verticalOffset}px ${shadow.blur}px)`,
        }}
    }

    if (style.shadow && style.image) {
        const shadow = style.shadow;
        const image = style.image;
        values = { ...values, ...{
            filter: `drop-shadow(${shadow.color} ${shadow.horizontalOffset}px ${shadow.verticalOffset}px ${shadow.blur}px) hue-rotate(${image.hueRotate}deg) saturate(${image.saturate}%)`,
        }}
    }

    return values;
}

/**
 * @param {string} textStyle.color - text color
 * @param {number} textStyle.fontWeight - font weight
 * @param {string} textStyle.fontStyle - font style
 * 
 * @param {string} textStyle.textDecoration - text decoration
 * @param {string} textStyle.textAlign - text align
 * @param {string} textStyle.textTransform - text transform
 * @param {number} textStyle.fontSize - font size
 * 
 * @param {number} textStyle.stroke.width - stroke width
 * @param {string} textStyle.stroke.color - stroke color
 * 
 * @param {string} textStyle.shadow.color - shadow color
 * @param {number} textStyle.shadow.horizontalOffset - shadow horizontal offset
 * @param {number} textStyle.shadow.verticalOffset - shadow vertical offset
 * @param {number} textStyle.shadow.blur - shadow blur
 * 
 * @param {number} textStyle.lineHeight - line height
 * @param {number} textStyle.letterSpacing - letter spacing
 * @param {number} textStyle.wordSpacing - word spacing
 * @param {number} textStyle.transform.x - transform x
 * @param {number} textStyle.transform.y - transform y
 * @param {number} textStyle.fontBlockWidth - font block width
 * @returns 
 */
const getTextStyle = (textStyle) => {

    const style = mergeProps(defaultTextStyle, textStyle);

    return {
        fontFamily: `"${style.font}", sans-serif`,
        color: style.color,
        fontWeight: style.fontWeight,
        fontStyle: style.fontStyle,
        textDecoration: style.textDecoration,
        textAlign: style.textAlign,
        textTransform: style.textTransform,
        fontSize: `${style.fontSize}px`,
        strokeWidth: style.stroke.width,
        WebkitTextStroke: `${style.stroke.width}px ${style.stroke.color}`,
        textShadow: `${style.shadow.color} ${style.shadow.horizontalOffset}px ${style.shadow.verticalOffset}px ${style.shadow.blur}px`,
        lineHeight: style.lineHeight,
        letterSpacing: `${style.letterSpacing}px`,
        wordSpacing: `${style.wordSpacing}px`,
        transform: `translateX(${style.transform.x}px) translateY(${style.transform.y}px)`,
        width: `${style.fontBlockWidth}px`,
        paintOrder: 'stroke fill',
        "--stroke-width": `${style.textOutlineBackgroundSize}px`,
        "--stroke-color": style.textOutlineBackgroundColor
    }
}

const RouletteItemCard = ({ index, item, cardStyle = defaultCardStyle, borderStyle = defaultBorderStyle, textStyle = defaultTextStyle }) => {

    const background = useMemo(() => getCardStyle(cardStyle), [cardStyle]);
    const border = useMemo(() => getBorderStyle(borderStyle), [borderStyle]);
    const text = useMemo(() => getTextStyle(textStyle), [textStyle]);

    return (
        <Box key={item.id} className="roulette-item-card" align="center" justify="center">
            {borderStyle.outline && border.width > 0 && border.height > 0 && border.borderWidth > 0 && <Box className="roulette-item-card-outline" align="center" justify="center" >
                <SvgBorder
                    id={`${index}_item-border`}
                    width={border.width} 
                    height={border.height} 
                    borderWidth={border.borderWidth} 
                    radius={border.radius} 
                    gradient={[
                        { offset: "0%", color: border.color1 },
                        { offset: "100%", color: border.color2 }
                    ]}
                    gradientAngle={border.gradientAngle}
                    style={{ filter: border.filter }}
                />
            </Box>}
            {borderStyle.image && <Box className="roulette-item-card-image" style={border}/>}
            <Box justify="center" align="center" style={background}>
                <Text className="roulette-item-card-text" data-text={item.name} style={text}>{item.name}</Text>
            </Box>
        </Box>
    )
}

export default memo(RouletteItemCard);